import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTool, FiClock, FiArrowRight, FiHeart, FiUsers, FiBookOpen, FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import Swal from 'sweetalert2';

// Logo importeren
import logoLions from '../assets/images/Logolions.jpg';

// Firebase Analytics en Firestore
import { analytics, logEvent, db } from '../firebase';
import { shouldShowAnalytics } from '../utils/cookieConsent';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const pulse = {
  hidden: { scale: 1 },
  visible: { 
    scale: [1, 1.05, 1],
    transition: { 
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

interface Activiteit {
  id: string;
  titel: string;
  datum: string;
  tijd: string;
  locatie: string;
  beschrijving: string;
  partnerToegestaan: boolean;
}

const UnderConstruction: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pracActiviteiten, setPracActiviteiten] = useState<Activiteit[]>([]);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Google Analytics - pagina view
  useEffect(() => {
    if (analytics && shouldShowAnalytics()) {
      logEvent(analytics, 'page_view', {
        page_title: 'Under Construction',
        page_location: window.location.href,
        page_path: '/'
      });
    }
  }, []);

  // PRAC activiteiten laden
  useEffect(() => {
    const loadPracActiviteiten = async () => {
      try {
        const vandaag = new Date();
        vandaag.setHours(0, 0, 0, 0);
        
        const activiteitenRef = collection(db, 'activiteiten');
        const q = query(
          activiteitenRef,
          where('datum', '>=', vandaag.toISOString().split('T')[0]),
          orderBy('datum', 'asc'),
          limit(2)
        );
        
        const snapshot = await getDocs(q);
        const activiteiten: Activiteit[] = [];
        snapshot.forEach((doc) => {
          activiteiten.push({
            id: doc.id,
            ...doc.data()
          } as Activiteit);
        });
        
        setPracActiviteiten(activiteiten);
      } catch (error) {
        console.error('Error loading PRAC activiteiten:', error);
        // Fallback naar hardcoded activiteiten als Firebase niet beschikbaar is
        setPracActiviteiten([
          {
            id: 'fallback-1',
            titel: 'Dansfeestlions', 
            datum: '2025-11-16',
            tijd: '16:00',
            locatie: 'De Nadorst, Blokker',
            beschrijving: 'Eerste dansfeest door ons georganiseerd met live muziek op een zondagmiddag van 16 tot 20 uur. De muziek komt uit de jaren 50 en 60. Meer informatie vindt u op <button class="btn-primary bg-lions-blue text-white hover:bg-lions-yellow px-4 py-2 rounded-lg" onclick="window.open(\'https://dansfeestlions.nl\', \'_blank\')">Dansfeest Lions Hoorn De Doelen</button>',
            partnerToegestaan: true
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadPracActiviteiten();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 8000); // 8 seconden per slide

    return () => clearInterval(interval);
  }, []);

  // Video afspelen wanneer slide 0 actief wordt
  useEffect(() => {
    if (currentSlide === 0 && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch((error) => {
        console.error('Video play error:', error);
      });
    }
  }, [currentSlide]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Functie om fallback activiteiten te updaten (kan worden aangeroepen door admin)
  const updateFallbackActiviteiten = (nieuweActiviteiten: Activiteit[]) => {
    setPracActiviteiten(nieuweActiviteiten);
  };

  // Expose functie voor admin gebruik
  React.useEffect(() => {
    (window as any).updateCarouselActiviteiten = updateFallbackActiviteiten;
  }, []);

  // Functie om SweetAlert popup te tonen voor niet-ingelogde gebruikers
  const showMembershipPopup = () => {
    Swal.fire({
      title: 'Word lid van Lions Hoorn De Doelen',
      text: 'Wordt lid van Lions Hoorn De Doelen om te zien wat we nog meer doen de komende maanden.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Toon meer informatie',
      cancelButtonText: 'Later',
      background: '#1e293b',
      color: '#ffffff',
      customClass: {
        popup: 'dark:bg-gray-800',
        title: 'text-white'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Navigeer naar lid worden pagina
        window.location.href = '/lid-worden-zonder-header-footer';
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-800 to-blue-900 relative overflow-hidden">
      {/* Achtergrond patroon */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* Hoofdinhoud */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Logo */}
            <motion.div 
              variants={pulse}
              className="mb-8"
            >
              <img 
                src={logoLions} 
                alt="Lions Club Hoorn De Doelen" 
                className="w-32 h-32 mx-auto rounded-lg shadow-2xl border-4 border-white/20"
              />
            </motion.div>

            {/* Carousel */}
            <div className="relative mb-8">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <AnimatePresence mode="wait">
                  {currentSlide === 0 && (
                    <motion.div
                      key="slide-0"
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gradient-to-br from-blue-800/50 to-indigo-800/50 backdrop-blur-sm p-8 rounded-2xl border border-white/20"
                    >
                      {/* Slide 1: Video en titel */}
                      <div className="mb-6 w-80 md:w-96 mx-auto">
                        <video 
                          ref={videoRef}
                          autoPlay 
                          muted
                          playsInline
                          loop
                          className="w-full aspect-video rounded-lg shadow-2xl border-4 border-white/20"
                          onError={(e) => {
                            console.error('Video error:', e);
                            const videoElement = e.target as HTMLVideoElement;
                            const parent = videoElement.parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <img 
                                  src="${logoLions}" 
                                  alt="Lions Club Hoorn De Doelen" 
                                  class="w-full aspect-video rounded-lg shadow-2xl border-4 border-white/20 object-cover"
                                />
                              `;
                            }
                          }}
                          onLoadedData={() => {
                            if (videoRef.current && currentSlide === 0) {
                              videoRef.current.play().catch((error) => {
                                console.error('Video play error on load:', error);
                              });
                            }
                          }}
                        >
                          <source src="/videos/Maninpaknaarleeuw.mp4" type="video/mp4" />
                          <p className="text-white text-center p-4">
                            Uw browser ondersteunt geen video afspelen.
                          </p>
                        </video>
                      </div>
                      
                      <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                        Wij zoeken <span className="text-teal-400">nieuwe leden</span>
                      </h1>
                      <p className="text-xl md:text-2xl text-gray-200 mb-6">
                        Word lid van Lions Club Hoorn De Doelen
                      </p>
                      <div>
                        <Link 
                          to="/lid-worden-zonder-header-footer" 
                          className="inline-flex items-center bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                          Meer weten? <FiArrowRight className="ml-2" />
                        </Link>
                      </div>
                    </motion.div>
                  )}

                  {currentSlide === 1 && pracActiviteiten[0] && (
                    <motion.div
                      key="slide-1"
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gradient-to-br from-green-800/50 to-emerald-800/50 backdrop-blur-sm p-8 rounded-2xl border border-white/20"
                    >
                      {/* Slide 2: 1e PRAC activiteit */}
                      <div className="mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FiCalendar className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                          {pracActiviteiten[0].titel}
                        </h2>
                        <div className="text-lg text-gray-200 mb-4">
                          <p className="mb-2">
                            <span className="font-semibold">Datum:</span> {formatDate(pracActiviteiten[0].datum)}
                          </p>
                          <p className="mb-2">
                            <span className="font-semibold">Tijd:</span> {pracActiviteiten[0].tijd}
                          </p>
                          <p className="mb-4">
                            <span className="font-semibold">Locatie:</span> {pracActiviteiten[0].locatie}
                          </p>
                        </div>
                        {pracActiviteiten[0].beschrijving && (
                          <div
                            className="text-gray-300 mb-6 prose prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: pracActiviteiten[0].beschrijving }}
                          />
                        )}
                        <button 
                          onClick={showMembershipPopup}
                          className="inline-flex items-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                          Bekijk alle activiteiten <FiArrowRight className="ml-2" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {currentSlide === 2 && pracActiviteiten[1] && (
                    <motion.div
                      key="slide-2"
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gradient-to-br from-purple-800/50 to-pink-800/50 backdrop-blur-sm p-8 rounded-2xl border border-white/20"
                    >
                      {/* Slide 3: 2e PRAC activiteit */}
                      <div className="mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FiUsers className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                          {pracActiviteiten[1].titel}
                        </h2>
                        <div className="text-lg text-gray-200 mb-4">
                          <p className="mb-2">
                            <span className="font-semibold">Datum:</span> {formatDate(pracActiviteiten[1].datum)}
                          </p>
                          <p className="mb-2">
                            <span className="font-semibold">Tijd:</span> {pracActiviteiten[1].tijd}
                          </p>
                          <p className="mb-4">
                            <span className="font-semibold">Locatie:</span> {pracActiviteiten[1].locatie}
                          </p>
                        </div>
                        {pracActiviteiten[1].beschrijving && (
                          <div
                            className="text-gray-300 mb-6 prose prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: pracActiviteiten[1].beschrijving }}
                          />
                        )}
                        <button 
                          onClick={showMembershipPopup}
                          className="inline-flex items-center bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                          Bekijk alle activiteiten <FiArrowRight className="ml-2" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Carousel Controls */}
              <div className="flex justify-center mt-6 space-x-2">
                {[0, 1, 2].map((slide) => (
                  <button
                    key={slide}
                    onClick={() => setCurrentSlide(slide)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentSlide === slide 
                        ? 'bg-white scale-125' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + 3) % 3)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300 backdrop-blur-sm"
              >
                <FiChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % 3)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300 backdrop-blur-sm"
              >
                <FiChevronRight className="h-6 w-6" />
              </button>
            </div>

            {/* Status indicator */}
            <motion.div 
              variants={fadeIn}
              className="mb-12"
            >
              <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <FiClock className="h-5 w-5 text-orange-400 mr-3 animate-pulse" />
                <span className="text-white font-medium">We zijn druk bezig met het bouwen van onze nieuwe website. We verwachten in oktober 2025 de nieuwe website online te hebben.</span>
              </div>
            </motion.div>

            {/* Wat we doen */}
            {/* <motion.div 
              variants={fadeIn}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
                Wat we aan het bouwen zijn
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <FiHeart className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Moderne Design</h3>
                  <p className="text-gray-200 text-sm">Een frisse, eigentijdse uitstraling die past bij onze missie</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <FiUsers className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Betere Ervaring</h3>
                  <p className="text-gray-200 text-sm">Verbeterde navigatie en gebruiksvriendelijkheid</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <FiBookOpen className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Meer Content</h3>
                  <p className="text-gray-200 text-sm">Uitgebreide informatie over onze projecten en activiteiten</p>
                </div>
              </div>
            </motion.div> */}

            {/* Call to Action */}
            {/* <motion.div 
              variants={fadeIn}
              className="mb-8"
            >
              <h3 className="text-xl font-bold text-white mb-4">
                Nieuwsgierig naar de huidige versie?
              </h3>
              <Link 
                to="/nieuw" 
                className="inline-flex items-center bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Bekijk huidige website <FiArrowRight className="ml-2" />
              </Link>
            </motion.div> */}

            {/* Contact info */}
            <motion.div 
              variants={fadeIn}
              className="text-gray-300"
            >
              <p className="mb-2">
                <strong>Lions Club Hoorn De Doelen</strong>
              </p>
              <p className="text-sm">
                Voor vragen kunt u contact met ons opnemen via{' '}
                <a href="mailto:secretaris@mail.lionshoorn.nl" className="text-teal-400 hover:text-teal-300 underline">
                  secretaris@mail.lionshoorn.nl
                </a>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Floating elements */}
      <div className="absolute top-20 left-10 opacity-20">
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-4 h-4 bg-teal-400 rounded-full"></div>
        </motion.div>
      </div>
      <div className="absolute top-40 right-20 opacity-20">
        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-6 h-6 bg-orange-400 rounded-full"></div>
        </motion.div>
      </div>
      <div className="absolute bottom-40 left-20 opacity-20">
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
        </motion.div>
      </div>
    </div>
  );
};

export default UnderConstruction; 